import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, euro, downloadReport, formatDate, formatMonth } from "@/lib/api";
import { Plus, Pencil, Trash2, CheckCircle2, Printer, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { bimbo_id: "", descrizione: "", importo: "", data: new Date().toISOString().slice(0, 10), metodo: "Bonifico", stato: "pagato", riferimento: "" };
const STATI = { pagato: ["Pagato", "bg-emerald-100 text-emerald-700 border-emerald-300"], in_attesa: ["In attesa", "bg-amber-100 text-amber-700 border-amber-300"], scaduto: ["Scaduto", "bg-red-100 text-red-600 border-red-300"] };

export default function Incassi() {
  const [incassi, setIncassi] = useState([]);
  const [bimbi, setBimbi] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [mese, setMese] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [sezione, setSezione] = useState("");
  const [ricerca, setRicerca] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/incassi", { params: { ...(filtro ? { stato: filtro } : {}), anno, ...(mese ? { mese: `${anno}-${mese}` } : {}), ...(sezione ? { sezione } : {}), ...(ricerca.trim() ? { ricerca: ricerca.trim() } : {}) } }).then((r) => setIncassi(r.data)).catch(() => {});
    api.get("/bimbi").then((r) => setBimbi(r.data)).catch(() => {});
  }, [filtro, anno, mese, sezione, ricerca]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, importo: parseFloat(form.importo) || 0, bimbo_id: form.bimbo_id || null };
      if (editing) await api.put(`/incassi/${editing.id}`, payload);
      else await api.post("/incassi", payload);
      toast.success("Incasso salvato");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const segnaPagato = async (i) => {
    await api.put(`/incassi/${i.id}`, { bimbo_id: i.bimbo_id, descrizione: i.descrizione, importo: i.importo, data: i.data, metodo: i.metodo, stato: "pagato", riferimento: i.riferimento || "" });
    toast.success("Segnato come pagato");
    load();
  };

  const remove = async (i) => {
    const isRetta = /^Retta\s/i.test(i.descrizione || "");
    const message = isRetta
      ? `Eliminare la retta di questo bambino per ${formatMonth((i.data || "").slice(0, 7))}? Non verrà ricreata automaticamente per questo mese.`
      : "Eliminare questo incasso?";
    if (!window.confirm(message)) return;
    const { data } = await api.delete(`/incassi/${i.id}`);
    toast.success(data.retta_esclusa ? "Retta eliminata per il mese selezionato" : "Incasso eliminato");
    load();
  };

  const segnaTuttiPagati = async () => {
    const daPagare = incassi.filter((i) => i.stato !== "pagato");
    if (daPagare.length === 0) return;
    if (!window.confirm(`Segnare come pagati ${daPagare.length} incassi in attesa?`)) return;
    try {
      await Promise.all(daPagare.map((i) =>
        api.put(`/incassi/${i.id}`, { bimbo_id: i.bimbo_id, descrizione: i.descrizione, importo: i.importo, data: i.data, metodo: i.metodo, stato: "pagato", riferimento: i.riferimento || "" })
      ));
      toast.success(`${daPagare.length} incassi segnati come pagati`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const totale = incassi.filter((i) => i.stato === "pagato").reduce((s, i) => s + i.importo, 0);
  const attesa = incassi.filter((i) => i.stato !== "pagato").reduce((s, i) => s + i.importo, 0);
  const totaleFiltrato = incassi.reduce((somma, incasso) => somma + (Number(incasso.importo) || 0), 0);
  const sezioni = [...new Set(bimbi.map((b)=>b.sezione).filter(Boolean))].sort((a,b)=>a.localeCompare(b,"it"));
  const mesi = [["","Tutti i mesi"],["01","Gennaio"],["02","Febbraio"],["03","Marzo"],["04","Aprile"],["05","Maggio"],["06","Giugno"],["07","Luglio"],["08","Agosto"],["09","Settembre"],["10","Ottobre"],["11","Novembre"],["12","Dicembre"]];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="incassi-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#3D405B] tracking-tight">Incassi</h1>
          <p className="text-[#6C757D] mt-1 text-sm">Rette e pagamenti delle famiglie</p>
        </div>
        <div className="flex gap-2"><button onClick={()=>downloadReport("incassi",{...(filtro?{filtro}:{}),anno})} className="btn-sage"><Printer className="w-4 h-4"/> Stampa PDF</button><button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-incasso-button" className="btn-warm">
          <Plus className="w-4 h-4" /> Registra incasso
        </button></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-warm p-5"><p className="label-warm">Incassato</p><p className="font-heading text-2xl font-semibold text-[#4a7a63] mt-1" data-testid="incassi-totale">{euro(totale)}</p></div>
        <div className="card-warm p-5 flex flex-col justify-between gap-2">
          <div><p className="label-warm">Da incassare</p><p className="font-heading text-2xl font-semibold text-amber-600 mt-1" data-testid="incassi-attesa">{euro(attesa)}</p></div>
          {attesa > 0 && (
            <button onClick={segnaTuttiPagati} data-testid="segna-tutti-pagati-button"
              className="flex items-center gap-1.5 self-start rounded-full bg-[#81B29A] px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#6ba188] transition">
              <CheckCircle2 className="w-3.5 h-3.5" /> Segna tutti pagati
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2" data-testid="incassi-filter-tabs">
        {[["", "Tutti"], ["pagato", "Pagati"], ["in_attesa", "In attesa"], ["scaduto", "Scaduti"]].map(([v, l]) => (
          <button key={v} onClick={() => setFiltro(v)} data-testid={`incassi-filter-${v || "tutti"}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${filtro === v ? "bg-[#3D405B] text-white" : "bg-white border border-[#e8d9c8] text-[#6C757D] hover:border-[#E07A5F]"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="card-warm p-4 grid sm:grid-cols-2 lg:grid-cols-[120px_190px_220px_1fr] gap-3 items-end">
        <div><label className="label-warm block mb-1.5">Anno</label><input type="number" min="2000" max="2100" value={anno} onChange={e=>setAnno(e.target.value)} className="input-warm" /></div>
        <div><label className="label-warm block mb-1.5">Mese</label><select value={mese} onChange={e=>setMese(e.target.value)} className="input-warm">{mesi.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></div>
        <div><label className="label-warm block mb-1.5">Sezione</label><select value={sezione} onChange={e=>setSezione(e.target.value)} className="input-warm"><option value="">Tutte le sezioni</option>{sezioni.map((item)=><option key={item} value={item}>{item}</option>)}</select></div>
        <div><label className="label-warm block mb-1.5">Cerca bambino</label><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a89a8a]"/><input value={ricerca} onChange={e=>setRicerca(e.target.value)} className="input-warm !pl-10" placeholder="Nome, cognome o codice fiscale..."/></div></div>
      </div>

      <div className="card-warm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="incassi-table">
            <thead>
              <tr className="border-b border-[#f0e4d7] text-center">
                <th className="px-5 py-3.5 label-warm">Data</th>
                <th className="px-5 py-3.5 label-warm">Descrizione</th>
                <th className="px-5 py-3.5 label-warm hidden md:table-cell">Bimbo</th>
                <th className="px-5 py-3.5 label-warm hidden md:table-cell">Metodo</th>
                <th className="px-5 py-3.5 label-warm">Importo</th>
                <th className="px-5 py-3.5 label-warm">Stato</th>
                <th className="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody>
              {incassi.map((i) => (
                <tr key={i.id} className="border-b border-[#faf3ea] hover:bg-[#FAF6F0] transition" data-testid={`incasso-row-${i.id}`}>
                  <td className="px-5 py-3.5 text-[#6C757D]">{formatDate(i.data)}</td>
                  <td className="px-5 py-3.5 font-semibold text-[#3D405B]">{i.descrizione}</td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-[#6C757D]">{i.bimbo_nome || "—"}{i.bimbo_sezione&&<div className="text-[11px] text-[#a89a8a]">{i.bimbo_sezione}{i.bimbo_cf?` · CF ${i.bimbo_cf}`:""}</div>}</td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-[#6C757D]">{i.metodo}</td>
                  <td className="px-5 py-3.5 font-bold text-[#3D405B]">{euro(i.importo)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATI[i.stato]?.[1] || ""}`} data-testid={`stato-${i.id}`}>{STATI[i.stato]?.[0] || i.stato}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1 justify-end">
                      {i.stato !== "pagato" && (
                        <button onClick={() => segnaPagato(i)} title="Segna pagato" data-testid={`paga-${i.id}`} className="p-2 rounded-lg text-[#4a7a63] hover:bg-[#E8F3EE] transition"><CheckCircle2 className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => { setEditing(i); setForm({ ...EMPTY, ...i, bimbo_id: i.bimbo_id || "", importo: String(i.importo) }); setOpen(true); }} data-testid={`edit-incasso-${i.id}`} className="p-2 rounded-lg text-[#3D405B] hover:bg-[#f0e4d7] transition"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(i)} data-testid={`delete-incasso-${i.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {incassi.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-[#a89a8a]" data-testid="incassi-empty">Nessun incasso registrato.</td></tr>}
            </tbody>
            {incassi.length > 0 && <tfoot><tr className="border-t-2 border-[#e8d9c8] bg-[#FAF6F0]"><td colSpan={4} className="px-5 py-4 text-right font-heading font-semibold text-[#3D405B]">Totale risultati filtrati ({incassi.length})</td><td className="px-5 py-4 text-lg font-bold text-[#4a7a63]" data-testid="incassi-totale-filtrato">{euro(totaleFiltrato)}</td><td colSpan={2}></td></tr></tfoot>}
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="incasso-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#3D405B]">{editing ? "Modifica incasso" : "Registra incasso"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="incasso-form">
            <div><label className="label-warm block mb-1.5">Descrizione</label><input required value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="incasso-descrizione-input" className="input-warm" placeholder="Es. Retta giugno 2026" /></div>
            <div><label className="label-warm block mb-1.5">Bimbo (opzionale)</label>
              <select value={form.bimbo_id} onChange={(e) => { const b=bimbi.find(x=>x.id===e.target.value); setForm({ ...form, bimbo_id: e.target.value, ...(b ? {importo:String(b.retta_mensile || ""), descrizione:`Retta ${formatMonth((form.data || new Date().toISOString().slice(0,10)).slice(0,7))}`} : {}) }); }} data-testid="incasso-bimbo-select" className="input-warm">
                <option value="">Nessuno / altro</option>
                {bimbi.map((b) => <option key={b.id} value={b.id}>{b.nome} {b.cognome}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-warm block mb-1.5">Importo €</label><input type="number" step="0.01" min="0" required value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="incasso-importo-input" className="input-warm" /></div>
              <div><label className="label-warm block mb-1.5">Data</label><input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="incasso-data-input" className="input-warm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-warm block mb-1.5">Metodo</label>
                <select value={form.metodo} onChange={(e) => setForm({ ...form, metodo: e.target.value })} data-testid="incasso-metodo-select" className="input-warm">
                  <option>Bonifico</option><option>Contanti</option><option>Carta</option><option>Addebito SDD</option>
                </select>
              </div>
              <div><label className="label-warm block mb-1.5">Stato</label>
                <select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })} data-testid="incasso-stato-select" className="input-warm">
                  <option value="pagato">Pagato</option><option value="in_attesa">In attesa</option><option value="scaduto">Scaduto</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} data-testid="incasso-cancel-button" className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#6C757D] hover:bg-[#f0e4d7] transition">Annulla</button>
              <button type="submit" data-testid="incasso-save-button" className="btn-warm">Salva</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
